import { Icon } from '@clarion/ClarionIcon';
import { ReactNode, SelectHTMLAttributes, useId } from 'react';
import { useFocusRing } from 'react-aria';
import { tv } from 'tailwind-variants';
import {
  ClarionDescription,
  ClarionFieldError,
  ClarionLabel,
  fieldBorderStyles,
} from './ClarionField';
import { focusRing } from './utils';

export interface ClarionHTMLSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  description?: string;
  errorMessage?: string;
  fill?: boolean;
  minimal?: boolean;
  size?: 'small' | 'medium' | 'large';
  iconProps?: any;
  options?: Array<{ label: string; value: string | number; disabled?: boolean }>;
  children?: ReactNode;
}

const selectWrapperStyles = tv({
  extend: focusRing,
  base: 'h-8 relative flex items-center box-border bg-white dark:bg-neutral-900/50 border rounded-lg overflow-hidden transition',
  variants: {
    isFocusWithin: fieldBorderStyles.variants.isFocusWithin,
    isDisabled: {
      true: 'opacity-50 cursor-not-allowed bg-neutral-100 dark:bg-neutral-800',
    },
    fill: {
      true: 'w-full',
    },
    minimal: {
      true: 'border-transparent dark:border-transparent bg-transparent dark:bg-transparent shadow-none',
    },
    size: {
      small: 'h-6',
      medium: 'h-8',
      large: 'h-10',
    },
  },
});

export function ClarionHTMLSelect({
  label,
  description,
  errorMessage,
  fill,
  minimal,
  size = 'medium',
  iconProps,
  options,
  children,
  className,
  ...props
}: ClarionHTMLSelectProps) {
  const id = useId();
  const { isFocusVisible, isFocused, focusProps } = useFocusRing({ within: true });

  return (
    <div className={`flex flex-col gap-1 font-sans ${fill ? 'w-full' : ''}`}>
      {label && <ClarionLabel htmlFor={props.id || id}>{label}</ClarionLabel>}
      <div
        className={selectWrapperStyles({
          fill,
          minimal,
          isDisabled: props.disabled,
          isFocusVisible,
          isFocusWithin: isFocused,
          className,
          size,
        })}
      >
        <select
          id={props.id || id}
          {...props}
          {...focusProps}
          className="appearance-none bg-transparent border-0 outline-none w-full h-full px-3 pr-8 cursor-pointer text-sm text-neutral-800 dark:text-neutral-200 disabled:cursor-not-allowed"
        >
          {options
            ? options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))
            : children}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <Icon icon="chevron-down" className="text-neutral-500 dark:text-neutral-400" size={16} />
        </div>
      </div>
      {description && <ClarionDescription>{description}</ClarionDescription>}
      {errorMessage && <ClarionFieldError>{errorMessage}</ClarionFieldError>}
    </div>
  );
}
